/**
 * Reflects the responsive navigation drawer's state onto its trigger.
 *
 * This is deliberately the ONLY JavaScript this drawer adds. Opening, closing,
 * the focus trap, the inert background, Escape, the scroll lock and the
 * close-on-widening rule are all Horizon's `dialog-component` and the platform's
 * own `<dialog>.showModal()`. Adding a second focus-management system beside
 * that one is how two systems end up disagreeing about where focus is.
 *
 * What `dialog-component` does not do is tell the trigger what state it is in.
 * A `<button aria-expanded>` that never changes is worse than no attribute at
 * all: it actively tells a screen-reader user the menu is closed while it is
 * open. Horizon syncs the cart drawer's trigger the same way in
 * assets/header-actions.js, and this follows that precedent rather than
 * inventing one.
 *
 * The listeners go on the dialog-component itself, not on the document.
 * `DialogOpenEvent` and `DialogCloseEvent` are constructed with no options, so
 * they do not bubble -- a document-level listener hears nothing, which is
 * exactly what happened on the first attempt and showed up as a drawer that
 * opened correctly while its trigger still reported aria-expanded="false".
 *
 * The relationship is read from `aria-controls`, so a second drawer would work
 * without touching this file.
 */

function bind(trigger) {
  const id = trigger.getAttribute('aria-controls');
  if (!id) return;
  const target = document.getElementById(id);
  if (!target || target.dataset.bohoAriaSynced === 'true') return;
  target.dataset.bohoAriaSynced = 'true';

  const set = (expanded) => {
    for (const el of document.querySelectorAll('[aria-controls="' + CSS.escape(id) + '"]')) {
      el.setAttribute('aria-expanded', String(expanded));
    }
  };
  target.addEventListener('dialog:open', () => set(true));
  target.addEventListener('dialog:close', () => set(false));

  // The dialog can also close without the component's own path -- a form
  // submission, or the platform closing it. `close` does not bubble either, so
  // it is listened for where it fires.
  const dialog = target.querySelector('dialog');
  if (dialog) dialog.addEventListener('close', () => set(false));
}

function bindAll() {
  for (const trigger of document.querySelectorAll('[aria-controls][aria-expanded]')) bind(trigger);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindAll, { once: true });
} else {
  bindAll();
}
